import React, {
  useMemo, useState, useCallback,
} from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper';
import { Img } from 'react-image';
import { useSelector } from 'react-redux';
import { fromStore } from '../../selectors';
import { Loader, PizzaSizer } from '../../components';
import {
  getIdGood, getIdModGood, getImageUrl, getModifiedGood, getWeightAndMeasure,
} from '../../utils';
import { AddCartButton } from './AddCartButton';
import { GoodPrice } from './GoodPrice';
import { FavotiteButton } from './FavotiteButton';
import { Slide } from './Slide';

export function PizzaCard({ id }) {
  const navigate = useNavigate();
  const itemId = getIdGood(id);
  const item = useSelector(fromStore.goodsByIdSelector(itemId));
  const { modifications = [] } = item;
  const defaultModification = modifications.find((mod) => mod.id === item.activeMod);
  const [activeMod, setActiveMod] = useState(defaultModification);
  const cartId = activeMod ? getIdModGood(activeMod, itemId) : id;
  const good = activeMod ? getModifiedGood(item, activeMod.id) : item;
  const categories = useSelector(fromStore.denormalizedCategories);
  const weightData = getWeightAndMeasure(good.weight, item.measure);
  const weight = weightData.weight ? `${weightData.measure.title}: ${weightData.weight} ${weightData.measure.value}` : null;
  const weightdopData = getWeightAndMeasure(item.weightdop, item.measuredop);
  const weightdop = weightdopData.weight ? `${weightdopData.measure.title}: ${weightdopData.weight} ${weightdopData.measure.value}` : null;

  const onClickDetail = useCallback(() => {
    const newPath = categories.find((category) => category.id === item.category.id).key;
    navigate(`/menu/${newPath}/${itemId}`);
    window.ym(92424510, 'reachGoal', 'click-dish');

    window.dataLayer.push(
      {
        ecommerce: {
          currencyCode: 'RUB',
          detail: {
            products: {
              id: itemId,
              name: item.name,
              brand: 'Sushibox',
              category: item.category.title,
              price: item.price,
            },
          },
        },
      },
    );
  }, []);

  const renderImage = useMemo(() => (
    <div className="text-center">
      <Img
        alt={item.name}
        src={getImageUrl(item.image)}
        className="mw-100 card-rounded"
        loader={<Loader image />}
      />
    </div>
  ), [item.image]);

  const stopPropagation = useCallback((e) => {
    e.stopPropagation();
  }, []);

  const renderCarousel = useMemo(() => (
    <div onClick={stopPropagation} aria-hidden="true">
      <Swiper navigation modules={[Navigation, Pagination]}>
        <SwiperSlide>
          <Slide name={item.name} image={item.image} click={onClickDetail} />
        </SwiperSlide>
        <SwiperSlide>
          <Slide name={item.name} image={item.secondImage} click={onClickDetail} />
        </SwiperSlide>
      </Swiper>
    </div>
  ), [item.name, item.image, item.secondImage, onClickDetail, stopPropagation]);

  if (item.isStop) return null;

  return (
    <div key={id} onClick={onClickDetail} aria-hidden className="col-xl-4">
      <div className="card mb-xl-8 border-hover-dark cursor-pointer card-xl-stretch">
        <div className="card-header border-0 pt-5">
          <div className="d-flex flex-stack w-100 align-items-start">
            <h3 className="card-title align-items-start flex-column m-0">
              <span className="card-label fw-bolder text-dark">{item.name}</span>
              <div className="d-flex align-items-center mt-2">
                {weight && <span className="badge badge-dark">{weight}</span>}
                {weightdop && <span className="badge badge-secondary ms-1">{weightdop}</span>}
                {item.hot && <img title="Острое" className="ms-1 w-15px" src="/assets/media/pepper.png" alt="острое" />}
                {item.vegan && <img title="Веган" className="ms-1 w-15px" src="/assets/media/vegan.png" alt="веган" />}
              </div>
            </h3>
            <FavotiteButton id={String(item.id)} />
          </div>
        </div>
        <div className="card-body pt-5 d-flex flex-column justify-content-between h-100">
          {item.secondImage ? renderCarousel : renderImage}
          {activeMod && (
            <div className="text-center mt-4">
              <PizzaSizer
                items={modifications}
                selected={activeMod}
                onChange={(value) => setActiveMod(value)}
              />
            </div>
          )}
          <div className="d-flex flex-stack mt-10">
            <GoodPrice price={good.price} oldPrice={good.oldPrice} id={String(item.id)} />
            <AddCartButton isStop={item.isStop} id={cartId} item={item} />
          </div>
        </div>
      </div>
    </div>
  );
}

PizzaCard.propTypes = {
  id: PropTypes.string.isRequired,
};
